// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2, Users, Kanban, Package, DollarSign, FileText, FileSignature, Receipt, CreditCard, Settings
} from 'lucide-react';
import { TabAzienda } from './crm-master/TabAzienda';
import { TabClienti } from './crm-master/TabClienti';
import { TabPipeline } from './crm-master/TabPipeline';
import { TabProdotti, TabListiniPrezzi } from './crm-master/TabListino';
import { TabPreventivi } from './crm-master/TabPreventivi';
import { TabContratti } from './crm-master/TabContratti';
import { TabFatturazione } from './crm-master/TabFatturazione';
import { TabPagamenti } from './crm-master/TabPagamenti';
import { TabImpostazioni } from './crm-master/TabImpostazioni';
import { LeadConversionWizard } from './crm-master/PipelineEntryPanels';

// Task #3055 — EasyCRM: le 10 sezioni del CRM. SoT della navigazione laterale
// (consumata da EasyCrmLayout) e del routing per-sezione (`/master/crm/:section`).
export const CRM_SECTIONS = [
  { value: 'azienda', label: 'Azienda', icon: Building2 },
  { value: 'clienti', label: 'Clienti', icon: Users },
  { value: 'pipeline', label: 'Pipeline', icon: Kanban },
  { value: 'prodotti', label: 'Prodotti', icon: Package },
  { value: 'listini', label: 'Listini', icon: DollarSign },
  { value: 'preventivi', label: 'Preventivi', icon: FileText },
  { value: 'contratti', label: 'Contratti', icon: FileSignature },
  { value: 'fatturazione', label: 'Fatturazione', icon: Receipt },
  { value: 'pagamenti', label: 'Pagamenti', icon: CreditCard },
  { value: 'impostazioni', label: 'Impostazioni', icon: Settings },
] as const;

export type CrmSection = (typeof CRM_SECTIONS)[number]['value'];

const CRM_SECTION_VALUES: readonly string[] = CRM_SECTIONS.map((s) => s.value);
const DEFAULT_CRM_SECTION: CrmSection = 'azienda';

function isCrmSection(value: string | undefined): value is CrmSection {
  return value !== undefined && CRM_SECTION_VALUES.includes(value);
}

export default function CRMMaster() {
  const { section } = useParams<{ section?: string }>();
  const [clientConversionEntryId, setClientConversionEntryId] = useState<string | null>(null);

  const handleQuoteAccepted = (pipelineEntryId: string) => {
    setClientConversionEntryId(pipelineEntryId);
  };

  const active: CrmSection = isCrmSection(section) ? section : DEFAULT_CRM_SECTION;

  const renderSection = (): JSX.Element => {
    switch (active) {
      case 'azienda': return <TabAzienda />;
      case 'clienti': return <TabClienti />;
      case 'pipeline': return <TabPipeline />;
      case 'prodotti': return <TabProdotti />;
      case 'listini': return <TabListiniPrezzi />;
      case 'preventivi': return <TabPreventivi onQuoteAccepted={handleQuoteAccepted} />;
      case 'contratti': return <TabContratti />;
      case 'fatturazione': return <TabFatturazione />;
      case 'pagamenti': return <TabPagamenti />;
      case 'impostazioni': return <TabImpostazioni />;
    }
  };

  return (
    <div className="w-full">
      {renderSection()}

      <LeadConversionWizard
        open={!!clientConversionEntryId}
        onClose={() => setClientConversionEntryId(null)}
        entryId={clientConversionEntryId || ''}
        onConverted={() => setClientConversionEntryId(null)}
      />
    </div>
  );
}
